/**
 * PositioningGuideOverlay — Overlay component for face positioning guidance.
 * Shows an oval reference contour that changes color (red → green) when face is positioned.
 * Provides text instructions in Portuguese for positioning adjustments.
 * Auto-captures after 1s of stability.
 *
 * Validates: Requirements 1.1, 1.2, 1.3
 */
import { useEffect, useRef, useState, useCallback, RefObject } from 'react';
import { Camera, CheckCircle } from 'lucide-react';
import { useFaceDetection, FaceDetectionResult } from '../../hooks/useFaceDetection';

export interface PositioningGuideOverlayProps {
  onFacePositioned: (descriptor: Float32Array) => void;
  videoRef: RefObject<HTMLVideoElement>;
  isActive: boolean;
}

type PositionInstruction =
  | 'aproximar'
  | 'afastar'
  | 'mover_esquerda'
  | 'mover_direita'
  | 'mover_cima'
  | 'mover_baixo'
  | null;

const TARGET_CENTER_X = 0.5;
const TARGET_CENTER_Y = 0.4;
const TOLERANCE_X = 0.15;
const TOLERANCE_Y = 0.12;
const MIN_FACE_RATIO = 0.3;
const MAX_FACE_RATIO = 0.7;
const STABILITY_DURATION_MS = 1000;

function getInstruction(
  faceBox: { x: number; y: number; width: number; height: number },
  frameWidth: number,
  frameHeight: number
): PositionInstruction {
  const faceCenterX = (faceBox.x + faceBox.width / 2) / frameWidth;
  const faceCenterY = (faceBox.y + faceBox.height / 2) / frameHeight;
  const faceRatio = faceBox.width / frameWidth;

  if (faceRatio < MIN_FACE_RATIO) return 'aproximar';
  if (faceRatio > MAX_FACE_RATIO) return 'afastar';
  if (faceCenterX < TARGET_CENTER_X - TOLERANCE_X) return 'mover_direita';
  if (faceCenterX > TARGET_CENTER_X + TOLERANCE_X) return 'mover_esquerda';
  if (faceCenterY < TARGET_CENTER_Y - TOLERANCE_Y) return 'mover_baixo';
  if (faceCenterY > TARGET_CENTER_Y + TOLERANCE_Y) return 'mover_cima';

  return null; // Positioned correctly
}

const instructionLabels: Record<string, string> = {
  aproximar: 'Aproxime o rosto da câmera',
  afastar: 'Afaste o rosto da câmera',
  mover_esquerda: 'Mova o rosto para a esquerda',
  mover_direita: 'Mova o rosto para a direita',
  mover_cima: 'Mova o rosto para cima',
  mover_baixo: 'Mova o rosto para baixo',
};

export function PositioningGuideOverlay({
  onFacePositioned,
  videoRef,
  isActive,
}: PositioningGuideOverlayProps) {
  const [instruction, setInstruction] = useState<PositionInstruction>(null);
  const [isPositioned, setIsPositioned] = useState(false);
  const [stabilityProgress, setStabilityProgress] = useState(0);
  const [lightingOk, setLightingOk] = useState(true);

  const stabilityStartRef = useRef<number | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const lastDescriptorRef = useRef<Float32Array | null>(null);

  const { detectFace, isModelLoaded } = useFaceDetection();

  const processFrame = useCallback(async () => {
    if (!isActive || !isModelLoaded) return;

    const result: FaceDetectionResult | null = await detectFace();

    if (!result) {
      setInstruction(null);
      setIsPositioned(false);
      setStabilityProgress(0);
      stabilityStartRef.current = null;
      return;
    }

    const video = videoRef.current;
    if (!video) return;

    const box = result.detection.box;
    const frameWidth = video.videoWidth || 640;
    const frameHeight = video.videoHeight || 480;

    const currentInstruction = getInstruction(
      { x: box.x, y: box.y, width: box.width, height: box.height },
      frameWidth,
      frameHeight
    );

    setInstruction(currentInstruction);

    // Check lighting via detection score
    const score = result.detection.score;
    setLightingOk(score > 0.6);

    if (currentInstruction === null) {
      // Face is positioned correctly
      setIsPositioned(true);
      lastDescriptorRef.current = result.descriptor;

      if (!stabilityStartRef.current) {
        stabilityStartRef.current = Date.now();
      }

      const elapsed = Date.now() - stabilityStartRef.current;
      const progress = Math.min(elapsed / STABILITY_DURATION_MS, 1);
      setStabilityProgress(progress);

      if (elapsed >= STABILITY_DURATION_MS) {
        // Auto-capture after 1s stability
        onFacePositioned(result.descriptor);
        stabilityStartRef.current = null;
        setStabilityProgress(0);
        return; // Stop loop after capture
      }
    } else {
      setIsPositioned(false);
      setStabilityProgress(0);
      stabilityStartRef.current = null;
    }
  }, [isActive, isModelLoaded, detectFace, videoRef, onFacePositioned]);

  useEffect(() => {
    if (!isActive) return;

    let running = true;

    const loop = async () => {
      if (!running) return;
      await processFrame();
      if (running) {
        animationFrameRef.current = requestAnimationFrame(loop);
      }
    };

    // Run at ~15 FPS
    const interval = setInterval(() => {
      if (running) loop();
    }, 66);

    return () => {
      running = false;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      clearInterval(interval);
    };
  }, [isActive, processFrame]);

  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
      {/* Oval guide */}
      <div
        className={`
          w-56 h-72 rounded-[50%] border-4 transition-colors duration-300
          ${isPositioned ? 'border-green-500 shadow-[0_0_20px_rgba(34,197,94,0.3)]' : 'border-red-500 shadow-[0_0_20px_rgba(239,68,68,0.3)]'}
        `}
        role="img"
        aria-label={isPositioned ? 'Rosto posicionado corretamente' : 'Posicione seu rosto dentro do oval'}
      />

      {/* Stability progress ring */}
      {isPositioned && stabilityProgress > 0 && stabilityProgress < 1 && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          <svg className="w-64 h-80" viewBox="0 0 256 320">
            <ellipse
              cx="128"
              cy="160"
              rx="112"
              ry="144"
              fill="none"
              stroke="rgba(34, 197, 94, 0.5)"
              strokeWidth="4"
              strokeDasharray={`${stabilityProgress * 800} 800`}
              strokeLinecap="round"
              className="transition-all duration-100"
            />
          </svg>
        </div>
      )}

      {/* Instruction text */}
      <div className="mt-6 text-center">
        {instruction && (
          <p className="text-white text-lg font-medium bg-black/60 px-4 py-2 rounded-lg">
            {instructionLabels[instruction]}
          </p>
        )}
        {isPositioned && !instruction && (
          <div className="flex items-center gap-2 text-green-400 text-lg font-medium bg-black/60 px-4 py-2 rounded-lg">
            <CheckCircle className="w-5 h-5" />
            <span>Mantenha a posição...</span>
          </div>
        )}
        {!instruction && !isPositioned && isActive && (
          <p className="text-white/80 text-base bg-black/60 px-4 py-2 rounded-lg">
            Posicione seu rosto dentro do oval
          </p>
        )}
      </div>

      {/* Lighting indicator */}
      {!lightingOk && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-yellow-900/80 text-yellow-200 px-3 py-1.5 rounded-lg text-sm">
          <Camera className="w-4 h-4" />
          <span>Busque melhor iluminação</span>
        </div>
      )}
    </div>
  );
}
